import { getHostUrl } from "@/composables/store";
import { useUser } from "@/stores/user";
import { hasMonitoringAccess } from "@/utils/monitoringAccess";

// Number of companies the signed-in user monitors, shown in the navigation
// badge. It is loaded on the server after checking the user's Monitoring
// permission (middleware/auth.global.ts) and carried into the client
// through the payload; the client only fetches it itself when a client-side
// navigation reaches a nav that has never had it (a login that stays in the
// SPA). Pages that add or remove a monitor adjust the shared state directly.
export const FOLLOWED_COMPANY_COUNT_KEY = "ucc-followed-company-count";

function resolveToken(value: unknown): string {
	if (typeof value === "string") return value;
	if (value && typeof value === "object" && "value" in value) {
		const nested = (value as { value?: unknown }).value;
		return typeof nested === "string" ? nested : "";
	}
	return "";
}

export function useFollowedCompanyCount() {
	// auth.global invokes the loader from userLoad.then(), outside Nuxt's
	// server context. Capture composables during setup; read the live user and
	// token from that store when the delayed request actually starts.
	const userStore = useUser();
	const hostUrl = getHostUrl();
	const count = useState<number>(FOLLOWED_COMPANY_COUNT_KEY, () => 0);
	const loaded = useState<boolean>(`${FOLLOWED_COMPANY_COUNT_KEY}-loaded`, () => false);

	async function loadFollowedCompanyCount(): Promise<number> {
		const token = resolveToken(userStore.token);
		if (!token || !hasMonitoringAccess(userStore.user)) {
			count.value = 0;
			loaded.value = false;
			return 0;
		}
		try {
			const response = await fetch(`${hostUrl}/ucc/company-monitors?limit=1&offset=0`, {
				headers: { Authorization: `Bearer ${token}` },
			});
			if (!response.ok) throw new Error(`Monitoring count request failed (${response.status})`);
			const payload = await response.json();
			count.value = Math.max(0, Number(payload?.followed_company_count || 0));
			loaded.value = true;
		} catch (error) {
			console.error("Unable to load followed-company count", error);
			// Keep the last known count and let the client navigation retry a
			// failed server load instead of hydrating a permanent zero.
			loaded.value = false;
		}
		return count.value;
	}

	return { followedCompanyCount: count, followedCompanyCountLoaded: loaded, loadFollowedCompanyCount };
}
